import type { OnpageIssueView } from './url-detail-onpage-issues';
import type { EvidenceTone } from './page-evidence';

/**
 * How the Technical row of the evidence panel words itself once the site's own
 * template defects are set aside.
 *
 * A flat "8 issues" per page was mostly not about the page. On
 * `manyrequests.com` 2,597 of 3,172 issue mentions are the same handful of
 * template defects repeated across 336 pages; on `slidespeak.co` it is 3,604 of
 * 4,465. The two or three findings that were genuinely about *this* page sat
 * buried in a list of six that were about the theme.
 *
 * Pure and separate from `url-detail` for the same reason `page-evidence.ts`
 * is: the component owns the accessors, this owns the phrasing, and phrasing
 * with three states in it is worth pinning in a test rather than clicking
 * through to find.
 */

export interface TechnicalEvidenceCopy {
  /** The `.badge .badge--caps` verdict. */
  label: string;
  tone: EvidenceTone;
  /** One sentence with the numbers in it. Never empty. */
  fact: string;
  /** Mono chips under the fact — the page's own issues where there are any. */
  specifics: string[];
}

/** Chips are a glance, not a list to read. */
const MAX_CHIPS = 3;

function chips(rows: OnpageIssueView[]): string[] {
  return rows
    .slice(0, MAX_CHIPS)
    .map((i) => i.title)
    .concat(rows.length > MAX_CHIPS ? [`+${rows.length - MAX_CHIPS}`] : []);
}

/**
 * The Technical row's copy, in one of three deliberately unalike states.
 *
 * - **`siteWideCodes` null — the rollup has not answered.** The old undivided
 *   count. A page that claims "2 page-specific" before the domain answer lands
 *   is asserting something it does not know yet, and the flicker from a
 *   confident wrong number to a confident right one is worse than waiting.
 * - **Something left.** Lead with the page's own count, because that is the
 *   work, and keep the total beside it so the badge is not silently smaller
 *   than the list the operator can expand below it.
 * - **Nothing left.** A different verdict, not a smaller one. "0 PAGE-SPECIFIC"
 *   reads as a broken counter; "ALL SITE-WIDE" is a finding. It drops to `warn`
 *   even when the issues are critical, because the severity is real but there
 *   is genuinely nothing to do *here* — and an evidence row that sorts to the
 *   top of a page's attention list for someone else's job is a false alarm.
 */
export function technicalEvidenceCopy(input: {
  issues: OnpageIssueView[];
  /** The domain's hoisted codes, or null while unknown. Never an empty set for "unknown". */
  siteWideCodes: Set<string> | null;
  severity: 'critical' | 'warning';
}): TechnicalEvidenceCopy {
  const list = input.issues;
  const total = list.length;
  const isCritical = input.severity === 'critical';
  const noun = isCritical ? 'issue' : 'warning';
  const plural = (n: number) => (n === 1 ? noun : `${noun}s`);

  if (!input.siteWideCodes) {
    return {
      label: `${total} ${plural(total).toUpperCase()}`,
      tone: isCritical ? 'bad' : 'warn',
      fact: isCritical
        ? 'Blocking problems found on the page.'
        : 'Nothing blocking, but these add friction.',
      specifics: chips(list)
    };
  }

  const siteWide = input.siteWideCodes;
  const own = list.filter((i) => !siteWide.has(i.code));
  const lifted = total - own.length;

  if (own.length === 0) {
    return {
      label: 'ALL SITE-WIDE',
      tone: 'warn',
      fact: `All ${total} ${plural(total)} here are site-wide — nothing to fix on this page alone.`,
      // The site-wide ones, so the row is never a verdict with an empty panel
      // under it. They are what the sentence is about.
      specifics: chips(list)
    };
  }

  return {
    label: `${own.length} PAGE-SPECIFIC`,
    tone: isCritical ? 'bad' : 'warn',
    fact:
      lifted > 0
        ? `${own.length} of ${total} ${plural(total)} ${own.length === 1 ? 'is' : 'are'} specific to this page; the other ${lifted} ${lifted === 1 ? 'is' : 'are'} site-wide.`
        : `${own.length} ${plural(own.length)} specific to this page.`,
    specifics: chips(own)
  };
}


/**
 * One line per technical finding, each tagged with whose problem it is.
 *
 * The deep-dive row states the split in a sentence — "2 of 8 are specific to
 * this page; the other 6 are site-wide" — and then lists only the page's own,
 * because on a dense evidence panel three chips is a glance and eight is a
 * wall.
 *
 * A review surface wants the opposite of a glance: every finding, one line
 * each, and the split as a TAG rather than a sentence. The tag is the useful
 * part. A page-specific issue is work on this page; a site-wide one is a
 * template fix that will clear the same finding on hundreds of pages at once,
 * and an operator triaging forty-five URLs needs to tell those apart at a
 * glance rather than reading a count and inferring it.
 *
 * Everything is listed, deliberately: "show everything" is affordable once each
 * finding is a line rather than a bordered panel with its own buttons.
 *
 * `siteWideCodes` null means the domain rollup has not answered yet. Rather
 * than guess a scope — which would print a confident tag that later flips —
 * every row comes back `unknown`, and the caller is expected to be holding the
 * card back anyway on a surface that cares.
 */
export type TechnicalIssueScope = 'page' | 'site-wide' | 'unknown';

export interface TechnicalReviewIssue {
  code: string;
  title: string;
  scope: TechnicalIssueScope;
}

export function technicalReviewIssues(input: {
  issues: OnpageIssueView[];
  siteWideCodes: Set<string> | null;
}): TechnicalReviewIssue[] {
  const siteWide = input.siteWideCodes;
  return input.issues.map((issue) => ({
    code: issue.code,
    title: issue.title,
    scope: !siteWide ? 'unknown' : siteWide.has(issue.code) ? 'site-wide' : 'page'
  }));
}

/** The tag's words. Two states that must not read alike — see the note above. */
export function technicalScopeLabel(scope: TechnicalIssueScope): string | null {
  if (scope === 'page') return 'This page';
  if (scope === 'site-wide') return 'Site-wide';
  return null;
}
